import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import type { ProbeResult } from '../lib/api'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Checkbox } from './ui/checkbox'
import { cn } from '../lib/utils'

/**
 * AddContact is the two-step "add an agent by URL" flow: probe, then pick.
 *
 * The two steps are not ceremony. A URL can host several agents, and the user
 * has no way to know which without asking the server — so showing what is
 * actually there, and letting them choose, is the difference between adding a
 * contact and guessing at one. Nothing is stored until they pick.
 */
export function AddContact({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [url, setUrl] = useState('')
  const [token, setToken] = useState('')
  const [probe, setProbe] = useState<ProbeResult | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Reopening starts clean: a half-finished attempt from ten minutes ago is
  // never what the user means to resume.
  useEffect(() => {
    if (open) {
      setUrl('')
      setToken('')
      setProbe(null)
      setSelected(new Set())
    }
  }, [open])

  const runProbe = useMutation({
    mutationFn: () => api.probe(url.trim(), token.trim() ? { kind: 'bearer', token: token.trim() } : undefined),
    onSuccess: (result) => {
      setProbe(result)
      // Pre-selecting everything matches the common case — a server hosting one
      // or two agents, all of which the user wants — without hiding the choice.
      setSelected(new Set(result.agents.map((a) => a.id)))
    },
  })

  const add = useMutation({
    mutationFn: () =>
      api.addAgents({
        base_url: probe!.base_url,
        auth: token.trim() ? { kind: 'bearer', token: token.trim() } : undefined,
        agent_ids: [...selected],
      }),
    onSuccess: async () => {
      // Adding a contact opens its conversation server-side, so refetching the
      // list and jumping to the newest one lands the user where they were
      // heading anyway: talking to the agent they just added.
      await queryClient.invalidateQueries({ queryKey: ['conversations'] })
      await queryClient.invalidateQueries({ queryKey: ['agents'] })
      const conversations = await api.listConversations()
      onClose()
      if (conversations.length > 0) navigate(`/c/${conversations[0].id}`)
    },
  })

  const error = runProbe.error ?? add.error
  const message = error instanceof ApiError ? error.message : error ? 'Something went wrong.' : ''

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="add-contact-overlay">
        <DialogHeader>
          <DialogTitle>Add an agent</DialogTitle>
          <DialogDescription>Paste the URL of a server speaking the Mastra protocol.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Input
            placeholder="https://agent.example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && url.trim()) runProbe.mutate()
            }}
            autoFocus
            data-testid="agent-url"
          />
          <Input
            placeholder="API token (optional)"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="agent-token"
          />

          <Button onClick={() => runProbe.mutate()} disabled={!url.trim() || runProbe.isPending} data-testid="probe">
            {runProbe.isPending ? 'Looking up…' : 'Look up agents'}
          </Button>
        </div>

        {message && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{message}</div>
        )}

        {runProbe.isPending && (
          <div className="flex justify-center py-6">
            <div className="size-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
          </div>
        )}

        {probe && (
          <div>
            <p className="mb-2 text-sm font-medium">
              {probe.agents.length === 0
                ? 'That server hosts no agents.'
                : `Found ${probe.agents.length} agent${probe.agents.length === 1 ? '' : 's'}`}
            </p>

            <div className="flex flex-col gap-2">
              {probe.agents.map((agent) => {
                const checked = selected.has(agent.id)
                return (
                  <button
                    key={agent.id}
                    type="button"
                    className={cn(
                      'flex items-start gap-3 rounded-md border p-3 text-left',
                      checked ? 'border-primary bg-primary/5' : 'border-border',
                    )}
                    onClick={() =>
                      setSelected((prev) => {
                        const next = new Set(prev)
                        if (next.has(agent.id)) next.delete(agent.id)
                        else next.add(agent.id)
                        return next
                      })
                    }
                    data-testid="probe-agent"
                  >
                    <Checkbox checked={checked} className="pointer-events-none mt-0.5" tabIndex={-1} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{agent.name}</p>
                      {agent.description && (
                        <p className="text-xs text-muted-foreground">{agent.description}</p>
                      )}
                      {agent.model_id && (
                        <p className="mt-1 text-xs text-muted-foreground/70">
                          {agent.model_id}
                          {agent.tools?.length ? ` · ${agent.tools.length} tools` : ''}
                        </p>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>

            {probe.agents.length > 0 && (
              <Button
                className="mt-4 w-full"
                onClick={() => add.mutate()}
                disabled={selected.size === 0 || add.isPending}
                data-testid="add-selected"
              >
                {add.isPending ? 'Adding…' : `Add ${selected.size} agent${selected.size === 1 ? '' : 's'}`}
              </Button>
            )}
          </div>
        )}

        <Button className="w-full" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  )
}
